import { state } from '../state';
import { get, post } from '../api';
import { showToast } from '../components/toast';
import { showModal, closeModal, confirmModal } from '../components/modal';
import { iconShield, iconPlus, iconTrash, iconEdit } from '../icons';
import type { Role } from '../types';

function esc(str: string): string {
  const el = document.createElement('div');
  el.textContent = str;
  return el.innerHTML;
}

function escAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function safeHex(v: string | null | undefined): string {
  if (!v) return '';
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v) ? v : '';
}

interface PermissionDef { label: string; desc: string }

const PERMISSIONS: Array<[string, PermissionDef]> = [
  ['manage_server',     { label: 'Manage Server',     desc: "Change the server's name, icon, region, and description." }],
  ['manage_channels',   { label: 'Manage Channels',   desc: 'Create, edit, and delete channels in this server.' }],
  ['manage_roles',      { label: 'Manage Roles',      desc: 'Create and edit roles below this role, and assign them to members.' }],
  ['manage_members',    { label: 'Manage Members',    desc: 'Kick, ban, or timeout members. Also required to change nicknames.' }],
  ['create_jobs',       { label: 'Create Jobs',       desc: 'Open new jobs for the team to claim.' }],
  ['manage_jobs',       { label: 'Manage Jobs',       desc: 'Edit, close, or reassign any existing job.' }],
  ['manage_shifts',     { label: 'Manage Shifts',     desc: 'Schedule, edit, and cancel shifts for other members.' }],
  ['manage_agreements', { label: 'Manage Agreements', desc: 'Create and edit agreements; request or override acceptance.' }],
  ['manage_approvals',  { label: 'Manage Approvals',  desc: 'Approve or deny pending requests for other members.' }],
  ['view_logs',         { label: 'View Logs',         desc: 'Read the server activity log, including member and channel changes.' }],
  ['send_messages',     { label: 'Send Messages',     desc: 'Write and reply in channels that allow this role to speak.' }],
  ['mention_everyone',  { label: 'Mention Everyone',  desc: 'Use @everyone and @here mentions that notify the whole server.' }],
  ['download_attachments', { label: 'Download Attachments', desc: 'Download file attachments from channels. Per-channel overrides may further restrict this.' }],
];

const PRESET_COLORS = [
  '#16a34a', '#57f287', '#fee75c', '#eb459e', '#ed4245',
  '#f47b67', '#e67e22', '#1abc9c', '#3498db', '#9b59b6',
];

const ROLE_ICONS = ['', '\u{1F451}', '\u{1F6E1}\u{FE0F}', '\u{2B50}', '\u{1F525}', '\u{2696}\u{FE0F}', '\u{1F3AF}', '\u{1F4E2}', '\u{1F527}', '\u{1F4BB}', '\u{1F3A8}', '\u{1F3AE}'];

type RoleTab = 'display' | 'permissions' | 'members';

export function renderRoles(body: HTMLElement): void {
  const serverId = state.currentServer;
  if (!serverId) {
    body.innerHTML = '<div class="cl-empty-msg">Select a server first</div>';
    return;
  }

  body.innerHTML = `
    <div class="channel-header"><h1>${iconShield()} Roles</h1></div>
    <div class="roles-toolbar">
      <button class="btn btn-primary btn-sm" id="create-role-btn">${iconPlus()} Create Role</button>
    </div>
    <div id="roles-list"><p style="color:var(--fg-muted);padding:16px">Loading roles...</p></div>
  `;

  document.getElementById('create-role-btn')?.addEventListener('click', () => showRoleModal(null, serverId, body));
  loadRoles(serverId, body);
}

async function loadRoles(serverId: string, body: HTMLElement): Promise<void> {
  try {
    const roles = await get<Role[]>(`/api/roles?server_id=${encodeURIComponent(serverId)}`);
    state.roles = roles;
    renderRolesList(roles, serverId, body);
  } catch {
    const container = document.getElementById('roles-list');
    if (container) container.innerHTML = '<p style="color:var(--fg-muted);padding:16px">Could not load roles</p>';
  }
}

function renderRolesList(roles: Role[], serverId: string, body: HTMLElement): void {
  const container = document.getElementById('roles-list');
  if (!container) return;

  if (roles.length === 0) {
    container.innerHTML = '<p style="color:var(--fg-muted);padding:16px">No roles defined</p>';
    return;
  }

  const sorted = [...roles].sort((a, b) => b.position - a.position);

  let html = `<div class="roles-toolbar-meta">${sorted.length} role${sorted.length !== 1 ? 's' : ''}</div><div class="roles-grid">`;
  for (const role of sorted) {
    const perms = parsePermissions(role.permissions);
    const permCount = Object.values(perms).filter(Boolean).length;
    const hex = safeHex(role.color);
    const styleAttr = hex ? ` style="--role-color:${hex}"` : '';
    const colorDot = hex ? `<span class="role-color-dot" aria-hidden="true"></span>` : '';
    const iconHtml = (role.icon ?? '') ? `<span class="role-card-icon" aria-hidden="true">${esc(role.icon ?? '')}</span>` : '';

    html += `
      <div class="role-card${hex ? ' role-card-colored' : ''}" data-role-id="${escAttr(role.id)}"${styleAttr}>
        <div class="role-card-header">
          ${colorDot}
          ${iconHtml}
          <span class="role-card-name">${esc(role.name)}</span>
          <span class="role-card-pos">pos ${role.position}</span>
        </div>
        <div class="role-card-perms">${permCount} permission${permCount !== 1 ? 's' : ''}</div>
        <div class="role-card-actions">
          <button class="btn btn-secondary btn-sm" data-edit-role="${escAttr(role.id)}">${iconEdit()} Edit</button>
          ${role.name !== '@everyone' ? `<button class="btn btn-danger btn-sm" data-delete-role="${escAttr(role.id)}" aria-label="Delete ${escAttr(role.name)}">${iconTrash()}</button>` : ''}
        </div>
      </div>
    `;
  }
  html += '</div>';
  container.innerHTML = html;

  container.querySelectorAll<HTMLElement>('[data-edit-role]').forEach(btn => {
    btn.addEventListener('click', () => {
      const roleId = btn.dataset['editRole'];
      const role = roles.find(r => r.id === roleId);
      if (role) showRoleModal(role, serverId, body);
    });
  });

  container.querySelectorAll<HTMLElement>('[data-delete-role]').forEach(btn => {
    btn.addEventListener('click', () => {
      const roleId = btn.dataset['deleteRole'];
      if (!roleId) return;
      const role = roles.find(r => r.id === roleId);
      confirmModal(`Delete ${role?.name || 'role'}`, 'This role will be removed from all members. This cannot be undone.', async () => {
        try {
          await post('/api/roles/delete', { id: roleId });
          showToast('Role deleted');
          await loadRoles(serverId, body);
        } catch {
          showToast('Failed to delete role', 'err');
        }
      }, { confirmLabel: 'Delete', variant: 'danger' });
    });
  });
}

/* ── Wide role modal (SSS-style sidebar + content) ── */

function showRoleModal(role: Role | null, serverId: string, body: HTMLElement): void {
  const isEdit = role !== null;
  const title = isEdit ? `Edit Role — ${esc(role.name)}` : 'Create Role';
  const perms = isEdit ? parsePermissions(role.permissions) : {};
  let activeTab: RoleTab = 'display';

  const sidebarHtml = (tab: RoleTab): string => {
    const item = (k: RoleTab, label: string): string =>
      `<button class="us-nav-item ${k === tab ? 'us-nav-active' : ''}" data-role-tab="${k}" type="button">${label}</button>`;
    return `
      <div class="us-nav-title">Role Settings</div>
      ${item('display', 'Display')}
      ${item('permissions', 'Permissions')}
      ${isEdit ? item('members', 'Members') : ''}
    `;
  };

  const renderTabContent = (tab: RoleTab, pane: HTMLElement): void => {
    switch (tab) {
      case 'display': renderDisplayTab(pane, role); break;
      case 'permissions': renderPermissionsTab(pane, perms); break;
      case 'members': if (role) renderMembersTab(pane, role, serverId); break;
    }
  };

  showModal(`
    <div class="modal-header">
      <h2>${title}</h2>
      <button class="btn-icon" id="modal-close">${iconX()}</button>
    </div>
    <div class="modal-body">
      <div class="sss-shell">
        <aside class="sss-sidebar" id="role-sidebar">${sidebarHtml(activeTab)}</aside>
        <section class="sss-content" id="role-pane"></section>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" id="modal-cancel">Cancel</button>
      <button class="btn btn-primary" id="modal-save">${isEdit ? 'Save' : 'Create'}</button>
    </div>
  `, { wide: true });

  const modalEl = document.getElementById('modal');
  modalEl?.classList.add('modal-sss');

  const pane = document.getElementById('role-pane');
  if (pane) renderTabContent(activeTab, pane);

  const wireSidebar = (): void => {
    document.querySelectorAll<HTMLButtonElement>('#role-sidebar [data-role-tab]').forEach(btn => {
      btn.addEventListener('click', () => {
        activeTab = (btn.dataset['roleTab'] || 'display') as RoleTab;
        document.querySelectorAll<HTMLButtonElement>('#role-sidebar [data-role-tab]').forEach(b => {
          b.classList.toggle('us-nav-active', b.dataset['roleTab'] === activeTab);
        });
        if (pane) renderTabContent(activeTab, pane);
      });
    });
  };
  wireSidebar();

  const doClose = (): void => {
    modalEl?.classList.remove('modal-sss');
    closeModal();
  };

  document.getElementById('modal-close')?.addEventListener('click', doClose);
  document.getElementById('modal-cancel')?.addEventListener('click', doClose);
  document.getElementById('modal-save')?.addEventListener('click', async () => {
    const nameEl = document.getElementById('f-role-name') as HTMLInputElement | null;
    const name = nameEl?.value.trim() || '';
    if (!name) { showToast('Name is required', 'warn'); return; }
    const color = (document.getElementById('f-role-color') as HTMLInputElement | null)?.value || '';
    const icon = (document.getElementById('f-role-icon') as HTMLInputElement | null)?.value || '';
    const permissions = gatherPermissions();

    try {
      if (isEdit) {
        await post('/api/roles/update', { id: role.id, name, color, icon, permissions: JSON.stringify(permissions) });
        showToast('Role updated');
      } else {
        await post('/api/roles/add', { server_id: serverId, name, color, icon, permissions: JSON.stringify(permissions) });
        showToast('Role created');
      }
      doClose();
      await loadRoles(serverId, body);
    } catch {
      showToast(isEdit ? 'Failed to update role' : 'Failed to create role', 'err');
    }
  });
}

function renderDisplayTab(pane: HTMLElement, role: Role | null): void {
  const nameVal = role ? escAttr(role.name) : '';
  const colorVal = role?.color || '';
  const iconVal = role?.icon || '';

  const colorSwatches = PRESET_COLORS.map(c =>
    `<button type="button" class="color-swatch ${c === colorVal ? 'selected' : ''}" data-color="${c}" style="background:${c}" aria-label="${c}"></button>`
  ).join('');

  const iconOpts = ROLE_ICONS.map(icon => {
    const cls = icon === iconVal ? 'role-icon-opt selected' : 'role-icon-opt';
    const label = icon === '' ? '×' : icon;
    return `<button type="button" class="${cls}" data-role-icon="${escAttr(icon)}" title="${icon === '' ? 'No icon' : icon}">${esc(label)}</button>`;
  }).join('');

  pane.innerHTML = `
    <div class="sss-pane-header">
      <h3 class="sss-pane-title">Display</h3>
      <p class="sss-pane-sub">Customise how this role appears in the member list and chat.</p>
    </div>
    <div class="sss-form">
      <div class="form-group">
        <label class="form-label" for="f-role-name">Role Name</label>
        <input class="form-input" id="f-role-name" placeholder="e.g. Moderator" value="${nameVal}" maxlength="80" />
      </div>
      <div class="form-group">
        <label class="form-label">Role Color</label>
        <div class="color-picker" id="color-picker">${colorSwatches}</div>
        <div class="color-picker-custom" style="margin-top:8px">
          <input type="text" class="form-input" id="f-role-color-custom" placeholder="#RRGGBB" maxlength="7" spellcheck="false" autocomplete="off" value="${escAttr(colorVal)}" style="max-width:140px" />
          <span class="form-hint" style="margin-left:8px;font-size:12px;color:var(--fg-muted)">Or paste a custom hex.</span>
        </div>
        <input type="hidden" id="f-role-color" value="${escAttr(colorVal)}" />
      </div>
      <div class="form-group">
        <label class="form-label">Role Icon</label>
        <div class="role-icon-picker">${iconOpts}</div>
        <input type="hidden" id="f-role-icon" value="${escAttr(iconVal)}" />
      </div>
    </div>
  `;

  setupColorPicker();
  setupCustomColor();
  setupIconPicker();
}

function renderPermissionsTab(pane: HTMLElement, perms: Record<string, boolean>): void {
  const rows = PERMISSIONS.map(([key, def]) =>
    `<div class="form-toggle-row">
       <div class="form-toggle-row-body">
         <div class="form-toggle-row-title">${esc(def.label)}</div>
         <div class="form-toggle-row-desc">${esc(def.desc)}</div>
       </div>
       <label class="form-toggle">
         <input type="checkbox" data-perm="${key}" ${perms[key] ? 'checked' : ''} />
         <span class="form-toggle-pill"></span>
       </label>
     </div>`
  ).join('');

  pane.innerHTML = `
    <div class="sss-pane-header">
      <h3 class="sss-pane-title">Permissions</h3>
      <p class="sss-pane-sub">Control what members with this role can do.</p>
    </div>
    <div class="form-toggle-list">${rows}</div>
  `;
}

function renderMembersTab(pane: HTMLElement, role: Role, serverId: string): void {
  const members = state.serverMembers.filter(m => m.server_id === serverId);

  pane.innerHTML = `
    <div class="sss-pane-header">
      <h3 class="sss-pane-title">Members</h3>
      <p class="sss-pane-sub">Members with the <strong>${esc(role.name)}</strong> role.</p>
    </div>
    <div id="role-members-list" style="display:flex;flex-direction:column;gap:2px">
      <p style="color:var(--fg-muted);font-size:13px">Loading...</p>
    </div>
  `;

  void get<Array<{ person_id: string; role_ids: string[] }>>(`/api/server-members?server_id=${encodeURIComponent(serverId)}`)
    .then(memberData => {
      const list = document.getElementById('role-members-list');
      if (!list) return;

      const withRole = memberData.filter(m =>
        Array.isArray(m.role_ids) && m.role_ids.includes(role.id)
      );
      const withoutRole = memberData.filter(m =>
        !Array.isArray(m.role_ids) || !m.role_ids.includes(role.id)
      );

      if (members.length === 0) {
        list.innerHTML = '<p style="color:var(--fg-muted);font-size:13px">No members in this server.</p>';
        return;
      }

      const personName = (pid: string): string => {
        const p = state.people.find(pp => pp.id === pid);
        return p?.name || `Member-${pid.substring(0, 6)}`;
      };

      let html = '';
      if (withRole.length > 0) {
        html += `<div class="form-label" style="margin-bottom:4px;margin-top:8px">Has Role (${withRole.length})</div>`;
        for (const m of withRole) {
          const name = personName(m.person_id);
          html += `<div class="role-member-row">
            <span class="role-member-avatar">${esc(name.charAt(0).toUpperCase())}</span>
            <span class="role-member-name">${esc(name)}</span>
            <button class="btn btn-sm btn-danger" data-rm-role="${escAttr(m.person_id)}" style="padding:2px 8px;font-size:11px">Remove</button>
          </div>`;
        }
      }
      if (withoutRole.length > 0) {
        html += `<div class="form-label" style="margin-bottom:4px;margin-top:12px">Add Member</div>`;
        for (const m of withoutRole) {
          const name = personName(m.person_id);
          html += `<div class="role-member-row">
            <span class="role-member-avatar">${esc(name.charAt(0).toUpperCase())}</span>
            <span class="role-member-name">${esc(name)}</span>
            <button class="btn btn-sm btn-primary" data-add-role="${escAttr(m.person_id)}" style="padding:2px 8px;font-size:11px">Add</button>
          </div>`;
        }
      }

      list.innerHTML = html || '<p style="color:var(--fg-muted);font-size:13px">No members found.</p>';

      list.querySelectorAll<HTMLButtonElement>('[data-rm-role]').forEach(btn => {
        btn.addEventListener('click', () => {
          const pid = btn.dataset['rmRole'];
          if (!pid) return;
          void post('/api/roles/member/remove', { role_id: role.id, person_id: pid, server_id: serverId })
            .then(() => { showToast('Role removed'); renderMembersTab(pane, role, serverId); })
            .catch(e => showToast(String(e), 'err'));
        });
      });
      list.querySelectorAll<HTMLButtonElement>('[data-add-role]').forEach(btn => {
        btn.addEventListener('click', () => {
          const pid = btn.dataset['addRole'];
          if (!pid) return;
          void post('/api/roles/member/add', { role_id: role.id, person_id: pid, server_id: serverId })
            .then(() => { showToast('Role assigned'); renderMembersTab(pane, role, serverId); })
            .catch(e => showToast(String(e), 'err'));
        });
      });
    })
    .catch(() => {
      const list = document.getElementById('role-members-list');
      if (list) list.innerHTML = '<p style="color:var(--fg-muted);font-size:13px">Failed to load members.</p>';
    });
}

/* ── Shared helpers ── */

function iconX(): string {
  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
}

function setupColorPicker(): void {
  document.querySelectorAll<HTMLElement>('.color-swatch').forEach(swatch => {
    swatch.addEventListener('click', () => {
      document.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
      swatch.classList.add('selected');
      const colorInput = document.getElementById('f-role-color') as HTMLInputElement | null;
      if (colorInput) colorInput.value = swatch.dataset['color'] ?? '';
      const customInput = document.getElementById('f-role-color-custom') as HTMLInputElement | null;
      if (customInput) customInput.value = swatch.dataset['color'] ?? customInput.value;
    });
  });
}

function setupCustomColor(): void {
  const custom = document.getElementById('f-role-color-custom') as HTMLInputElement | null;
  if (!custom) return;
  custom.addEventListener('input', () => {
    let v = custom.value.trim();
    if (v && !v.startsWith('#')) v = '#' + v;
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      const colorInput = document.getElementById('f-role-color') as HTMLInputElement | null;
      if (colorInput) colorInput.value = v;
      document.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
    }
  });
}

function setupIconPicker(): void {
  document.querySelectorAll<HTMLElement>('.role-icon-opt').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.role-icon-opt').forEach(b => b.classList.remove('selected'));
      btn.classList.add('selected');
      const hidden = document.getElementById('f-role-icon') as HTMLInputElement | null;
      if (hidden) hidden.value = btn.dataset['roleIcon'] ?? '';
    });
  });
}

function gatherPermissions(): Record<string, boolean> {
  const perms: Record<string, boolean> = {};
  document.querySelectorAll<HTMLInputElement>('[data-perm]').forEach(cb => {
    const key = cb.dataset['perm'];
    if (key) perms[key] = cb.checked;
  });
  return perms;
}

function parsePermissions(json: string): Record<string, boolean> {
  try {
    return JSON.parse(json) as Record<string, boolean>;
  } catch {
    return {};
  }
}
